// Updated BookingDesktopView component with expandable first row

import React, { useState } from 'react';
import { Grid, Box, Typography, Button, useTheme } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SearchIcon from '@mui/icons-material/Search';
import ExploreIcon from '@mui/icons-material/Explore';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsIcon from '@mui/icons-material/Directions';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import dayjs from 'dayjs';
import { BookingRow, ActionButton, BookingsHeader } from './styled';
import { useProfile } from 'context';
import ContactHelpModal from 'components/organisms/contactHelpModal';
import { alpha } from '@mui/material/styles';

const BookingDesktopView = ({
  bookings,
  navigateBooking,
  navigateVenue,
  statusMapping,
  navigateSearch,
  showHeader = true,
  resultLimit = 5,
  handleCheckin,
  handleCheckout,
  expandNextBooking = false
}) => {
  const theme = useTheme();
  const { profile } = useProfile();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const handleOpenHelpModal = (venue) => {
    setSelectedVenue(venue);
    setHelpModalOpen(true);
  };

  const headers = [
    {
      field: 'planned_start_day',
      label: 'When',
      width: 3,
      icon: <CalendarTodayIcon fontSize="small" />
    },
    {
      field: 'status',
      label: 'Status',
      width: 2,
      icon: <InfoOutlinedIcon fontSize="small" />
    },
    {
      field: 'price',
      label: 'Cost',
      width: 1,
      icon: <AttachMoneyIcon fontSize="small" />
    },
    {
      field: 'code',
      label: 'Code',
      width: 1,
      icon: <QrCodeIcon fontSize="small" />
    },
    {
      field: 'venue__name',
      label: 'Space',
      width: 4,
      icon: <LocationOnIcon fontSize="small" />
    },
    {
      field: 'actions',
      label: '',
      width: 1
    }
  ];

  // Remove toggleExpand function as it's no longer needed

  const openInGoogleMaps = (venue) => {
    if (venue?.google_maps_url) {
      // Open the Google Maps URL directly - user can select directions there
      window.open(venue.google_maps_url, '_blank');
    } else {
      // Fallback to constructing a directions URL
      const query = encodeURIComponent(`${venue.name || ''}, ${venue.address || ''}`);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${query}`, '_blank');
    }
  };

  return (
    <>
      {/* Header */}
      {showHeader && (<BookingsHeader>
        <Box>
          <Typography variant="h5">
            <EventNoteIcon />
            Your Workspace Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your upcoming bookings
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={navigateSearch}
          sx={{
            borderRadius: '20px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          Book New Space
        </Button>
      </BookingsHeader>)}

      <Box sx={{
        backgroundColor: 'grey.50',
        borderRadius: theme.shape.borderRadius,
        mb: 2,
        p: 1
      }}>
        <Grid container spacing={2}>
          {headers.map(header => (
            <Grid item xs={header.width} key={header.field}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                {header.icon}
                {header.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Rows */}
      {bookings?.slice(0, resultLimit).map((item, index) => {
        const formattedStartTime = item?.planned_start_time ? dayjs(item.planned_start_time, 'HH:mm').format('HH:mm') : '';
        const formattedEndTime = item?.planned_end_time ? dayjs(item.planned_end_time, 'HH:mm').format('HH:mm') : '';
        const formattedDate = `${dayjs(item?.planned_start_day).format('DD/MM/YYYY')} ${formattedStartTime} - ${formattedEndTime}`;
        const price = (parseFloat(item.price) || 0)// + (parseFloat(item.vat_amount) || 0);
        const formattedPrice = isNaN(price) ? '' : `£${price.toFixed(2)}`;
        const isFirstBooking = index === 0;
        const showAdditionalInfo = expandNextBooking && isFirstBooking;

        // Create a dayjs object from the date string
        const bookingDate = dayjs(item?.planned_start_day);
        const dayOfWeek = bookingDate.format('dddd');
        const dayNumber = bookingDate.day() === 0 ? 6 : bookingDate.day() - 1;

        // Find the opening hours for the booking day
        const dayOpeningHours = item?.venue?.open_time?.find(schedule =>
          schedule.day === dayNumber && schedule.status === "AC"
        );

        // Format the opening hours
        const formattedOpeningHours = dayOpeningHours
          ? `${dayOfWeek} opens from ${dayOpeningHours.time_start.substring(0, 5)} to ${dayOpeningHours.time_end.substring(0, 5)}`
          : `${dayOfWeek} - Contact venue for opening hours`;

        return (
          <React.Fragment key={`f_${item.slug}`}>
            <BookingRow
              key={`r_${item.slug}`}
              sx={{
                borderBottomLeftRadius: showAdditionalInfo ? 0 : undefined,
                borderBottomRightRadius: showAdditionalInfo ? 0 : undefined,
                borderBottom: showAdditionalInfo ? 'none' : undefined,
                position: 'relative',
                transition: 'all 0.3s'
              }}
            >
              {showAdditionalInfo && (
                <React.Fragment key={`expanded_${item.id}`}>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0 }}>
                      🎯 Your next workspace is just {dayjs(item?.planned_start_day).diff(dayjs(), 'days')} days away, {profile?.first_name}!
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: 'background.paper',
                      borderBottomLeftRadius: theme.shape.borderRadius,
                      borderBottomRightRadius: theme.shape.borderRadius,
                      boxShadow: theme.shadows[0],
                      mb: 1,
                      mt: 0,
                      position: 'relative',
                      border: '0px solid',
                      borderTopColor: 'divider',
                      zIndex: 0
                    }}
                  >
                    <Grid container spacing={2}>
                      {/* Left Column (80%) - Venue Information */}
                      <Grid item xs={9} key={`venue-info-${item.id}`}>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={() => navigateVenue(item?.venue?.slug)}
                        >
                          {item?.venue?.name}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1.5 }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item?.venue?.single_address || 'Address not available'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1.5, mt: 0.3 }} />
                          <Typography variant="body2">
                            {formattedOpeningHours}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                          <MeetingRoomIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1.5, mt: 0.3 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                              Access instructions:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item?.venue?.access || 'No special instructions provided.'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Right Column (20%) - Actions */}
                      <Grid item xs={3} key={`venue-actions-${item.id}`} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <ActionButton
                          variant="outlined"
                          size="small"
                          fullWidth
                          startIcon={<DirectionsIcon />}
                          onClick={() => openInGoogleMaps(item?.venue)}
                        >
                          Get me there
                        </ActionButton>

                        <ActionButton
                          variant="outlined"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={<PhoneIcon />}
                          onClick={() => handleOpenHelpModal(item?.venue)}
                        >
                          Need help
                        </ActionButton>

                        <ActionButton
                          variant="outlined"
                          color={item.real_start ? "primary" : "success"}
                          size="small"
                          startIcon={<CheckIcon />}
                          onClick={() => handleCheckin && handleCheckin(item)}
                          disabled={!!item.real_start}
                          fullWidth
                        >
                          {item.real_start
                            ? `Checked in: ${dayjs(item.real_start).format('HH:mm')}`
                            : "Check-in"
                          }
                        </ActionButton>

                        <ActionButton
                          variant="outlined"
                          color={item.real_end ? "primary" : "error"}
                          size="small"
                          startIcon={<LogoutIcon />}
                          onClick={() => handleCheckout && handleCheckout(item)}
                          disabled={!!item.real_end || !item.real_start} // Disable if checked out or not checked in yet
                          fullWidth
                        >
                          {item.real_end
                            ? `Checked out: ${dayjs(item.real_end).format('HH:mm')}`
                            : "Check-out"
                          }
                        </ActionButton>
                      </Grid>
                    </Grid>
                  </Box>
                </React.Fragment>
              )}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <Typography variant="body2" fontWeight={500}>{formattedDate}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: (theme) => {
                        if (item?.status === 'AC') return alpha(theme.palette.green.sad, 0.08); // Active (Confirmed)
                        if (item?.status === 'PC') return alpha(theme.palette.primary.main, 0.08); // Pending Confirmation
                        if (item?.status === 'FN') return alpha(theme.palette.blue.sad, 0.08); // Finished
                        return alpha(theme.palette.error.main, 0.08); // Others like Cancelled, Rejected, etc.
                      },
                      color: (theme) => {
                        if (item?.status === 'AC') return theme.palette.green.sad; // Active (Confirmed)
                        if (item?.status === 'PC') return theme.palette.primary.main; // Pending Confirmation
                        if (item?.status === 'FN') return theme.palette.blue.sad; // Finished
                        return theme.palette.error.main; // Others
                      }
                    }}
                  >
                    <Typography variant="body2">{statusMapping[item?.status]}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="body2" fontWeight={500} color="primary.main">
                    {formattedPrice}
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography variant="body2">{item?.code}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2">{item?.venue?.name}</Typography>
                </Grid>
                <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionButton
                    variant="contained"
                    size="small"
                    onClick={() => navigateBooking(item)}
                  >
                    Details
                  </ActionButton>
                </Grid>
              </Grid>


            </BookingRow>
          </React.Fragment>
        );
      })}

      <ContactHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        venue={selectedVenue}
      />

      {/* Empty state */}
      {bookings.length < 5 && (
        <BookingRow
          sx={{
            display: 'flex',
            alignItems: 'center', // Changed to align items center
            justifyContent: 'center',
            minHeight: '200px',
            backgroundColor: 'grey.50',
            border: '2px dashed',
            borderColor: 'divider',
            boxShadow: 'none',
            gap: 2,
            position: 'relative',
            overflow: 'hidden',
            padding: 2, // Added padding
            '&:hover': {
              backgroundColor: 'grey.100',
              transform: 'none',
              '& .search-icon': {
                transform: 'scale(1.1) rotate(-5deg)'
              }
            }
          }}
        >
          {/* Icon Section - 25% width */}
          <Box
            className="search-icon"
            sx={{
              width: '25%', // Fixed width for icon section
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              position: 'relative',
              transition: 'transform 0.3s ease',
            }}
          >
            <SearchIcon
              sx={{
                fontSize: 80,
                color: theme.palette.primary.main,
                opacity: 0.2,
                position: 'absolute'
              }}
            />
            <ExploreIcon
              sx={{
                fontSize: 60,
                color: theme.palette.primary.main,
                position: 'absolute',
                marginLeft: '-20px',
                marginTop: '20px'
              }}
            />
          </Box>

          {/* Content Section - 75% width */}
          <Box
            sx={{
              width: '75%', // Fixed width for content
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{
                fontWeight: 'medium',
                textAlign: 'left' // Changed to left alignment
              }}
            >
              Ready to discover your next workspace?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 2,
                textAlign: 'left', // Changed to left alignment
                maxWidth: '100%'
              }}
            >
              Find the perfect spot for your next productive day! 🚀
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={navigateSearch}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                padding: '8px 24px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Explore Spaces
            </Button>
          </Box>
        </BookingRow>
      )}
    </>
  );
};

export default BookingDesktopView;